import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import Spinner from 'components/Spinner/Spinner';

interface DiagnosticsAuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
}

const DiagnosticsAuthContext = createContext<DiagnosticsAuthContextType>({
  isAuthenticated: false,
  token: null,
  login: () => {},
  logout: () => {},
});

function decodeJwtPayload(token: string): any {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return {};
  }
}

function isTokenExpired(token: string): boolean {
  try {
    const decoded: any = decodeJwtPayload(token);
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
}

export const DiagnosticsAuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem("diagnosticsToken");
    if (storedToken && !isTokenExpired(storedToken)) {
      setIsAuthenticated(true);
      setToken(storedToken);
    } else {
      localStorage.removeItem("diagnosticsToken");
      setIsAuthenticated(false);
      setToken(null);
    }
    setLoading(false);
  }, []);

  if (loading) return <Spinner />;

  const login = (token: string) => {
    localStorage.setItem("diagnosticsToken", token);
    setIsAuthenticated(true);
    setToken(token);
  };

  const logout = () => {
    localStorage.removeItem("diagnosticsToken");
    setIsAuthenticated(false);
    setToken(null);
  };

  return (
    <DiagnosticsAuthContext.Provider value={{ isAuthenticated, token, login, logout }}>
      {children}
    </DiagnosticsAuthContext.Provider>
  );
};

export const useDiagnosticsAuth = () => useContext(DiagnosticsAuthContext); 